import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { flatten } from 'lodash';
import { of } from 'rxjs';
import { Color, Icons, TableAppearance } from '@lsq-frontend/common-ui';
import {
    Document,
    DocumentService,
    FieldSchema,
    StageData,
    StageSchema
} from '@lsq-frontend/remote-services/ar-app';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { GenericJSON } from '../../service/dr-application-service.interface';
import { IFieldPair } from '../dynamic-container-base/dynamic-container-base.component.interfaces';
import { DrApplicationService } from '../../service/dr-application-service';

interface DocumentTable extends Partial<Document> {
    fieldLabel: string;
    hasDocumentField: boolean;
}
@Component({
    selector: 'dr-review-stage-base',
    template: ``
})
export class ReviewStageBase implements OnInit {
    @Input() formGroup: FormGroup;
    @Input() stageSchema: StageSchema;
    @Input() reviewMode: Boolean;
    @Input() adminMode: Boolean = false;
    public readonly icons = Icons;
    public documentsDataSource: LsqListSource<GenericJSON> = undefined;
    public appearance = TableAppearance.Default;
    public hasDocumentFields = false;
    public color = Color;
    public nonDocumentFields: IFieldPair[];
    public stageData: StageData;
    public prospectId = '';
    public loadingDownloadAll: boolean;

    constructor(
        public arService: DrApplicationService,
        private _documentService: DocumentService,
        private _arService: DrApplicationService
    ) {
        this.prospectId = this._arService.getProspect().id;
    }

    public ngOnInit(): void {
        this.loadStageData();
        this._arService.prospectSchemaSubject.subscribe(() => {
            var prospectSchema = this._arService.getProspectSchema();
            var newStages = prospectSchema.stageSchemas.filter(
                (stage) => stage.id === this.stageSchema.id
            );
            if (newStages.length > 0) {
                this.stageSchema = newStages[0];
                this.loadStageData();
            }
        });
    }

    public loadStageData() {
        this.nonDocumentFields = this.getNonDocumentFields();
        this.getDocumentFields();
        this.updateDocumentDataSource(this.getDocuments());
    }

    public updateDocumentDataSource(documents: DocumentTable[]): void {
        this.hasDocumentFields = documents.length !== 0;
        this.documentsDataSource = new LsqListSource<GenericJSON>({
            data$: of({ rows: documents }),
            rows$: of(documents)
        });
    }

    public getDocuments(): DocumentTable[] {
        const fieldsSchema = this.arService.plainFieldSchemas(this.stageSchema?.fieldSchemas ?? []);
        const documentFieldSchemas: FieldSchema[] = fieldsSchema.filter(
            (fieldSchema: FieldSchema) =>
                fieldSchema.fieldType === FieldSchema.FieldTypeEnum.DocumentField ||
                fieldSchema.fieldType === FieldSchema.FieldTypeEnum.DownloadAllDocumentsField ||
                fieldSchema.fieldType === FieldSchema.FieldTypeEnum.AddDocumentField ||
                fieldSchema.fieldType === FieldSchema.FieldTypeEnum.AddDocumentListField
        );
        const prospectFields = this.arService.filterStage(this.stageSchema.id).fields;
        const documentsTable = documentFieldSchemas.map((fieldSchema: FieldSchema) => {
            const documentField = prospectFields[fieldSchema.id];
            const hasDocument =
                documentField !== undefined &&
                !!documentField.fieldValue &&
                Array.isArray(documentField.fieldValue) &&
                documentField.fieldValue.length > 0;
            if (!hasDocument && !this.adminMode) {
                return null;
            } else if (!hasDocument) {
                return [
                    {
                        hasDocumentField: false,
                        fieldLabel: fieldSchema.label,
                        id: fieldSchema.id
                    }
                ];
            }

            const documentInstances: DocumentTable[] = documentField.fieldValue.map(
                (documentInstance: Document) => ({
                    hasDocumentField: true,
                    ...documentInstance,
                    fieldLabel: fieldSchema.label,
                    id: fieldSchema.id
                })
            );
            return documentInstances;
        });

        let documentTableFlatten = flatten(documentsTable);

        documentTableFlatten = documentTableFlatten.filter(
            (documentTable: DocumentTable) => !!documentTable
        );
        return documentTableFlatten;
    }

    public getNonDocumentFields(): IFieldPair[] {
        const fields = this.arService.plainFieldSchemas(this.stageSchema?.fieldSchemas ?? []);
        const blackList = [
            FieldSchema.FieldTypeEnum.DocumentField,
            FieldSchema.FieldTypeEnum.LabelField,
            FieldSchema.FieldTypeEnum.EditPhoneField,
            FieldSchema.FieldTypeEnum.AddDocumentField,
            FieldSchema.FieldTypeEnum.AddDocumentListField,
            FieldSchema.FieldTypeEnum.DownloadAllDocumentsField
        ];
        const prospectFields = this.arService.filterStage(this.stageSchema.id).fields;
        const fieldSchemas = fields.filter(
            (fieldSchema: FieldSchema) =>
                !blackList.includes(fieldSchema.fieldType) && !/password/.test(fieldSchema.id)
        );
        const queue = [];
        for (const fieldSchema of fieldSchemas) {
            const fields = prospectFields;
            queue.push({
                schema: fieldSchema,
                value: fields
            });
        }
        return queue;
    }

    public getDocumentFields(): void {
        const prospect = this.arService.initStages();
        this.stageData = prospect.stages[this.stageSchema.id];
    }

    public async downloadDocument(doc: Document): Promise<void> {
        this._arService.downloadDocument(doc.documentId, doc.fileName);
    }
}
